import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { posthog } from '../components/PostHogProvider';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Supabase automatically picks up the tokens from the URL hash
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error || !session) {
          console.error('Auth callback: No session found', error);
          toast.error('Authentication failed. Please try again.');
          navigate('/login');
          return;
        }

        const supabaseUser = session.user;
        console.log('Auth callback: Supabase user:', supabaseUser.email);

        // Send the Supabase user info to our backend to create/find user and set session cookie
        const response = await api.post('/auth/google-callback', {
          email: supabaseUser.email,
          name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
          picture: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || '',
          access_token: session.access_token
        });

        setUser(response.data.user);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('sb-token', response.data.session_token);

        // Track Google auth completion with latency
        const googleAuthStart = sessionStorage.getItem('google_auth_start');
        const latencyMs = googleAuthStart ? Date.now() - parseInt(googleAuthStart, 10) : null;
        sessionStorage.removeItem('google_auth_start');
        if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_POSTHOG_KEY) {
          posthog.capture('auth_completed', {
            auth_method: 'google',
            ...(latencyMs !== null && { latency_ms: latencyMs }),
          });
        }

        toast.success('Welcome to PurrCheck!');
        
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1000);
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processCallback();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]">
      <div className="text-center">
        <div className="w-10 h-10 border-[3px] border-[#1B4332] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#1B4332]/60 text-sm font-medium">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
